import React, { ReactNode } from 'react';
import { PVSystemData } from './components/PVSystemForm/PVSystemForm.helpers';
import {
  BatteryOptimizationProductConfiguration,
  CreateExistingCustomerOrderAcceptedInput,
  CreateExistingCustomerSwitchingFromFlatXTariffAcceptedInput,
  CreateProductConfigurationsInput,
  CurrencyProperty,
  CurrencyPropertyHourlySeriesPoint,
  CurrentUser as CurrentUserType,
  CurrentUserAccountBillingAddress,
  CurrentUserSite,
  CurrentUserSiteAddress,
  CustomerSalutation,
  CustomerUserAccountType,
  EnergyDynamicProductConfigurationWithBatteryOptimizationModule,
  EnergyDynamicProductConfigurationWithoutBatteryOptimizationModule,
  EnergyFixProductConfigurationWithFeedInModule,
  EnergyFixProductConfigurationWithoutFeedInModule,
  InputMaybe,
  Maybe,
  ProductConfigurations,
  ProviderChangeReason,
  TerminationPeriod,
} from './graphql/generated';

export enum OrderFlow {
  energyDynamic = 'energyDynamic',
  energyFix = 'energyFix',
  selfConsumptionPlus = 'selfConsumptionPlus',
  batteryOptimization = 'batteryOptimization',
  directMarketingUpselling = 'directMarketingUpselling',
}

/**
 * Local copy of the CurrentUser type from the GraphQL schema.
 * Properties omitted here are not yet used in the production schema.
 */
export type CurrentUser = CurrentUserType;

export enum ContractCreationBlockedReason {
  notAvailable = 'notAvailable',
  sonnenHome = 'sonnenHome',
}

export type TFormValues = TDeliveryDetails &
  TPaymentDetails & {
    deliveryAddress: TAddress;
    personalData: TPersonalData;
  } & {
    interestedInHardwareOffer: boolean;
    confirmedDynamicTariff?: boolean;
    billingAddress: TAddress;
  } & CreateExistingCustomerOrderAcceptedInput &
  CreateExistingCustomerSwitchingFromFlatXTariffAcceptedInput & {
    marketing?: boolean;
    confirmedDynamicTariff?: boolean;
  };

export type SelectOptionType<T = string> = {
  label: string;
  value: T;
  additionalText?: string;
};

export type TPersonalData = {
  salutation?: CustomerSalutation;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName?: string;
} & TTaxData;

export const TTaxStatus = {
  standard: 'standard',
  smallBusiness: 'smallBusiness',
} as const;

export type TTaxStatus = (typeof TTaxStatus)[keyof typeof TTaxStatus];

export type TTaxData = {
  taxId?: string;
  taxStatus?: TTaxStatus;
};

export type TPersonalInfoData = {
  salutation?: CustomerSalutation;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName?: string;
};

export type TDeliveryDetails = {
  meterId: string;
  marketLocationId?: string;
  contractTerminated: boolean;
  terminatedEnergyProviderName?: string;
  terminatedEnergyProviderId?: string;
  confirmedCancellationDate?: Date;
  deliveryStartDate?: Date;
  providerChangeReason?: ProviderChangeReason;
};

export type TPaymentDetails = {
  iban: string;
  accountOwner: string;
};

export type TAddress = {
  city?: InputMaybe<string>;
  country: string;
  postalCode: string;
  streetName?: InputMaybe<string>;
  streetNumber?: InputMaybe<string>;
};

export type TCompleteAddress = {
  city: string;
  country: string;
  postalCode: string;
  streetName: string;
  streetNumber: string;
};

export type AppContextType = {
  accountType: CustomerUserAccountType;
  currentBackground?: PageBackground;
  formValues: TFormValues;
  offerRequiresManualAddress: boolean;
  contractCreationBlocked: ContractCreationBlockedReason | null;
  orderComplete: boolean;
  selectedSite: CurrentUserSite | null;
  setAccountType: React.Dispatch<React.SetStateAction<AppContextType['accountType']>>;
  setFormValues: React.Dispatch<React.SetStateAction<AppContextType['formValues']>>;
  setOfferRequiresManualAddress: React.Dispatch<
    React.SetStateAction<AppContextType['offerRequiresManualAddress']>
  >;
  setContractCreationBlocked: React.Dispatch<
    React.SetStateAction<AppContextType['contractCreationBlocked']>
  >;
  setOrderComplete: React.Dispatch<React.SetStateAction<AppContextType['orderComplete']>>;
  setSelectedSite: React.Dispatch<React.SetStateAction<AppContextType['selectedSite']>>;
  setUser: React.Dispatch<React.SetStateAction<AppContextType['user']>>;
  setUserProcessing: React.Dispatch<React.SetStateAction<AppContextType['userProcessing']>>;
  user: CurrentUser | null;
  userProcessing: boolean;
  /**
   * The set of modules this user has selected.
   */
  modules: Set<ModuleName>;
  addModule: (module: ModuleName) => void;
  removeModule: (module: ModuleName) => void;
  toggleModule: (module: ModuleName) => void;
  customBillingAddress: boolean;
  setCustomBillingAddress: (value: boolean) => void;
  orderFlow: OrderFlow | null;
  setOrderFlow: (value: OrderFlow | null) => void;
  currentModal?: ReactNode;
  setCurrentModal: (value?: ReactNode) => void;
  setCurrentBackground: (background?: PageBackground) => void;
  resetOrderState: () => void;
  showVisitorForm: boolean;
  setShowVisitorForm: React.Dispatch<React.SetStateAction<boolean>>;
};

export type UserAddress = keyof CurrentUserAccountBillingAddress | keyof CurrentUserSiteAddress;

export enum OfferErrorTranslationKey {
  SERVER = 'errorServer',
  FAILED_TO_CREATE_CONFIGURATION = 'errorConfig',
  POSTCODE_REQUIRES_ADDRESS = 'errorRequiresAddress',
  INVALID_CUSTOMER_ADDRESS = 'errorInvalidCustomerAddress',
}
export type OfferErrorTranslationKeyType = OfferErrorTranslationKey | null;

export type OfferContextType = {
  quarterlyPriceData?: CurrencyPropertyHourlySeriesPoint[] | null;
  offer: ProductConfigurations | null;
  offerError: OfferErrorTranslationKeyType;
  offerLoading: boolean;
  productConfigurationsInput: CreateProductConfigurationsInput;
  /**
   * Whenever you change this input, the offer is reset and reloaded.
   */
  setProductConfigurationsInput: (input: CreateProductConfigurationsInput) => void;
  personsInHousehold: number;
  setPersonsInHousehold: (persons: number) => void;
  workEmail: string;
  setWorkEmail: (value: string) => void;
  setOffer: (offer: ProductConfigurations | null) => void;
  setOfferLoading: (loading: boolean) => void;
  setOfferError: (error: OfferErrorTranslationKeyType) => void;
  pvSystemData: PVSystemData | null;
  setPvSystemData: (data: PVSystemData | null) => void;
  pvFormState: PVSystemData;
  setPvFormState: (data: PVSystemData) => void;
  resetPvState: () => void;
  /** True when the user has submitted fully-filled PV data (year, month, capacity, feedIn all present). */
  hasPvData: boolean;
};

export enum ModuleName {
  PRICE_OPTIMIZATION = 'priceOptimization',
  DIRECT_MARKETING = 'directMarketing',
  VPP = 'vpp',
}

export type TPriceCard = {
  title: string;
  caption: string;
  key?: string;
};

export type TariffConfiguration =
  | EnergyFixProductConfigurationWithFeedInModule
  | EnergyFixProductConfigurationWithoutFeedInModule
  | EnergyDynamicProductConfigurationWithBatteryOptimizationModule
  | EnergyDynamicProductConfigurationWithoutBatteryOptimizationModule
  | BatteryOptimizationProductConfiguration;

export type DayAheadPriceConfiguration = {
  dayAheadPriceBaseFeeGrossPerMonth?: Maybe<CurrencyProperty>;
  dayAheadPriceBenefitDetailsAmount?: Maybe<CurrencyProperty>;
  dayAheadPriceTerminationPeriodOption?: Maybe<TerminationPeriod>;
};

export enum PageBackground {
  HOME = 'home',
  CONFIRMATION = 'confirmation',
  CONTRACT_CREATION_BLOCKED = 'contractCreationBlocked',
}
